// @ts-nocheck
import { Card, Code, Divider, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { ImageGallery, FileUpload, MediaPlayer } from '../../../components/advanced/media';

export default function MediaPage() {
  // Image gallery data (public placeholder images, lightbox opens only on click)
  const galleryImages = [
    {
      src: 'https://picsum.photos/id/1018/1200/800',
      thumbnail: 'https://picsum.photos/id/1018/400/300',
      title: 'Mountain Vista',
      description: 'Snow-capped peaks at sunrise',
    },
    {
      src: 'https://picsum.photos/id/1015/1200/800',
      thumbnail: 'https://picsum.photos/id/1015/400/300',
      title: 'River Bend',
      description: 'A quiet river winding through the valley',
    },
    {
      src: 'https://picsum.photos/id/1019/1200/800',
      thumbnail: 'https://picsum.photos/id/1019/400/300',
      title: 'Forest Path',
      description: 'Morning light through the trees',
    },
    {
      src: 'https://picsum.photos/id/1016/1200/800',
      thumbnail: 'https://picsum.photos/id/1016/400/300',
      title: 'Canyon Walls',
      description: 'Layered rock formations',
    },
    {
      src: 'https://picsum.photos/id/1024/1200/800',
      thumbnail: 'https://picsum.photos/id/1024/400/300',
      title: 'Coastline',
      description: 'Waves meeting the shore at dusk',
    },
    {
      src: 'https://picsum.photos/id/1036/1200/800',
      thumbnail: 'https://picsum.photos/id/1036/400/300',
      title: 'Open Field',
      description: 'Rolling hills under a wide sky',
    },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Media Components</Title>
        <Text c="dimmed" size="sm">
          Components for uploading files, browsing image galleries, and playing audio or video
        </Text>
      </Stack>

      {/* Image Gallery */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ImageGallery</Title>
            <Text size="sm" c="dimmed">
              Responsive image grid with hover captions and a built-in fullscreen lightbox
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <ImageGallery
              images={galleryImages}
              columns={3}
              withLightbox
              withZoom
              onImageClick={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const images = [
  {
    src: 'https://picsum.photos/id/1018/1200/800',
    thumbnail: 'https://picsum.photos/id/1018/400/300',
    title: 'Mountain Vista',
    description: 'Snow-capped peaks at sunrise',
  },
  // ... more images
];

<ImageGallery
  images={images}
  columns={3}
  withLightbox
  withZoom
  onImageClick={(image, index) => {
    // Handle image click
  }}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • images - Array of gallery images (src, title, description)
              <br />
              • columns - Number of grid columns
              <br />
              • withLightbox - Open fullscreen viewer on click
              <br />
              • withZoom - Enable zoom controls in the lightbox
              <br />
              • withDownload - Show a download button
              <br />
              • onImageClick - Image click handler
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Image Interface:</Text>
            <Code block language="typescript">
{`interface GalleryImage {
  src: string;
  alt?: string;
  thumbnail?: string;
  title?: string;
  description?: string;
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* File Upload */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>FileUpload</Title>
            <Text size="sm" c="dimmed">
              Drag-and-drop dropzone with previews, upload progress, and per-file status
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <FileUpload
              label="Attachments"
              description="Upload supporting documents or images"
              maxFiles={5}
              maxSize={5 * 1024 * 1024}
              multiple
              onChange={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const [files, setFiles] = useState([]);

<FileUpload
  value={files}
  onChange={setFiles}
  label="Attachments"
  description="Upload supporting documents or images"
  maxFiles={5}
  maxSize={5 * 1024 * 1024}
  multiple
  onUpload={async (file) => {
    // Upload file and return { url }
    const url = await uploadToServer(file);
    return { url };
  }}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • value - Controlled array of uploaded files
              <br />
              • onChange - Fires when the file list changes
              <br />
              • onUpload - Async per-file upload (returns a url)
              <br />
              • maxFiles - Maximum number of files
              <br />
              • maxSize - Maximum file size in bytes
              <br />
              • variant - 'dropzone' | 'button' | 'compact'
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>File Interface:</Text>
            <Code block language="typescript">
{`interface UploadedFile {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  url?: string;
  preview?: string;
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Media Player - Video */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>MediaPlayer</Title>
            <Text size="sm" c="dimmed">
              Custom video and audio player with seek, volume, loop, picture-in-picture, and fullscreen
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (Video):</Text>
            <MediaPlayer
              type="video"
              src="https://test-videos.co.uk/vids/bigbuckbunny/mp4/h264/360/Big_Buck_Bunny_360_10s_1MB.mp4"
              poster="https://picsum.photos/id/1043/800/450"
              title="Sample Video"
              width="100%"
            />
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (Audio):</Text>
            <MediaPlayer
              type="audio"
              src="https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3"
              title="Sample Audio Track"
              width="100%"
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`// Video
<MediaPlayer
  type="video"
  src="/media/intro.mp4"
  poster="/media/intro-poster.jpg"
  title="Intro Video"
  width="100%"
  onPlay={() => console.log('playing')}
  onEnded={() => console.log('ended')}
/>

// Audio
<MediaPlayer
  type="audio"
  src="/media/announcement.mp3"
  title="Weekly Announcement"
  width="100%"
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • src - Media source URL (required)
              <br />
              • type - 'video' | 'audio'
              <br />
              • poster - Poster image for video
              <br />
              • title - Title (shown for audio)
              <br />
              • autoPlay / loop / muted - Playback flags
              <br />
              • onPlay / onPause / onEnded / onTimeUpdate - Event handlers
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Document Attachments</Text>
              <Text size="xs" c="dimmed">
                Use FileUpload to attach receipts, contracts, or images to records with live progress and validation.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Photo Galleries</Text>
              <Text size="xs" c="dimmed">
                Use ImageGallery for event photos, product shots, or property listings with a fullscreen lightbox.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Training & Media</Text>
              <Text size="xs" c="dimmed">
                Use MediaPlayer to embed training videos, announcements, or podcasts with full playback controls.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
